import React from "react";
import Language from "../lib/Language";
import NumberInput from "./NumberInput";
import MultiSelect, { DIVIDER as MultiSelectDivider } from "./MultiSelect";
import ListSettingsStore from "./ListSettingsStore";
import API from "../lib/TimeEditAPI";
import _ from "underscore";
import RC from "../lib/ReservationConstants";
import {
    TAvailableCategories,
    TCategorySelection,
    TOrderListSettingsValues,
} from "../types/components/SearchOptions";
import { TFieldDefClass } from "../types/api/APIClassTypes";

const getStatus = () => [
    {
        label: `${Language.get("order_status_row_partial")} (<)`,
        trackingId: "Partial",
        id: 1,
        isDefault: true,
    },
    {
        label: `${Language.get("order_status_row_fulfilled")} (=)`,
        trackingId: "Fulfilled",
        id: 2,
        isDefault: true,
    },
    {
        label: `${Language.get("order_status_row_exceeded")} (>)`,
        trackingId: "Exceeded",
        id: 3,
        isDefault: true,
    },
    { label: `${Language.get("order_status_row_unlimited")} (o)`, trackingId: "Unlimited", id: 4 },
    { label: `${Language.get("order_status_passive")} (-)`, trackingId: "Passive", id: 5 },
    { label: Language.get("order_status_not_completed"), trackingId: "Not completed", id: 102 },
    { label: Language.get("order_status_completed"), trackingId: "Completed", id: 101 },
    {
        label: `${Language.get("dynamic_reserv_list_delivered")} = 0`,
        trackingId: "Delivered = 0",
        id: 111,
    },
    {
        label: `${Language.get("dynamic_reserv_list_delivered")} <> 0`,
        trackingId: "Delivered <> 0",
        id: 112,
    },
];

const getSearch = () => ({
    0: { id: 0, label: Language.get("cal_order_side_order_objects"), trackingId: "Order objects" },
    1: {
        id: 1,
        label: Language.get("dynamic_reserv_list_mandatory_object"),
        trackingId: "Mandatory object",
    },
    2: {
        id: 2,
        label: Language.get("dynamic_reserv_list_customer_object"),
        trackingId: "Customer object",
    },
    3: {
        id: 3,
        label: Language.get("dynamic_reserv_list_owner_object"),
        trackingId: "Owner object",
    },
    4: {
        id: 4,
        label: Language.get("cal_reservation_list_column_order_additional_objects"),
        trackingId: "Additional objects",
    },
});

const getIncludeMembers = () => ({
    0: { id: 0, label: Language.get("dynamic_reserv_list_exclude"), trackingId: "Exclude" }, //"MEMBER_PARENT_GROUPS", // Föräldergrupper
    1: { id: 1, label: Language.get("dynamic_reserv_list_include"), trackingId: "Include" }, //"MEMBER_SUBGROUPS", // Föräldergrupper + undergrupper
    2: { id: 2, label: Language.get("dynamic_reserv_list_obstacles"), trackingId: "Obstacles" }, //"MEMBER_OBSTACLES", // Föräldergrupper + undergrupper + förhinder
    3: {
        id: 3,
        label: Language.get("dynamic_reserv_list_members_only"),
        trackingId: "Members only",
    }, //"MEMBER_NO_GROUPS", // Inga grupper
    4: {
        id: 4,
        label: Language.get("dynamic_reserv_list_obstacles_only"),
        trackingId: "Obstacles only",
    }, //"MEMBER_OBSTACLES_NO_SUBGROUPS", // Föräldergrupper + förhinder
});

/**
 * OrderList builds this panel in getSettingsComponent: it passes the search
 * options it currently holds, the category and field lists to choose from, and
 * the four callbacks ListSettingsStore needs for named searches.
 */
type TOrderListSettingsProps = Omit<TOrderListSettingsValues, "complete"> & {
    onChange: (settings: TOrderListSettingsState) => void;
    saveDefaultSettings?: () => void;
    saveSettings: (...args: any[]) => void;
    getSavedSettings: (callback: (result: any[]) => void) => void;
    removeSettings: (...args: any[]) => void;
    /** The reservation fields offered as the description and comment columns. */
    reservationFields: TFieldDefClass[];
} & Pick<TAvailableCategories, "availableObjectCategories" | "availableObjectCheckboxes">;

/**
 * The edited copy of the values, handed back through onChange whenever it
 * changes.
 */
type TOrderListSettingsState = Omit<TOrderListSettingsValues, "complete" | "objectCategories"> & {
    /** Seeded from the props and edited here, so always an object. */
    objectCategories: TCategorySelection;
};

class OrderListSettings extends React.Component<TOrderListSettingsProps, TOrderListSettingsState> {
    constructor(props) {
        super(props);
        this.state = this._getInitState(props);
    }

    componentDidUpdate(prevProps, prevState) {
        if (!_.isEqual(this.state, prevState)) {
            const settings = _.clone(this.state);
            this.props.onChange(settings);
        }
    }

    _getInitState = (props = this.props) => {
        return {
            // Search options
            orderStatus: props.orderStatus,
            objectSearch: props.objectSearch,
            permission: props.permission,
            useFactor: props.factor > 1,
            factor: props.factor,
            includeMembers: props.includeMembers,
            objectCategories: props.objectCategories || {},
            descriptionField: props.descriptionField || null,
            commentField: props.commentField || null,
        };
    };

    onDescriptionFieldChange = (event) => {
        this.setState({ descriptionField: parseInt(event.target.value, 10) || null });
    };

    onCommentFieldChange = (event) => {
        this.setState({ commentField: parseInt(event.target.value, 10) || null });
    };

    onObjectSearchChange = (values) => {
        this.setState({ objectSearch: values });
    };

    onStatusChange = (values) => {
        this.setState({ orderStatus: values });
    };

    onPermissionChange = (event) => {
        this.setState({ permission: parseInt(event.target.value, 10) });
    };

    saveFactor = (newFactor) => {
        API.setPreferences("orderListFactor", [`${newFactor}`], _.noop);
    };

    onFactorToggle = () => {
        const useFactor = !this.state.useFactor;
        // eslint-disable-next-line no-magic-numbers
        const factor = useFactor ? 36 : 1;
        if (factor !== this.state.factor) {
            this.saveFactor(factor);
        }
        this.setState({ useFactor, factor });
    };

    onFactorChange = (newFactor) => {
        if (newFactor !== this.state.factor) {
            this.saveFactor(newFactor);
            this.setState({ factor: newFactor });
        }
    };

    onIncludeMembersChange = (event) => {
        const value = parseInt(event.target.value, 10);
        this.setState({ includeMembers: value });
    };

    onObjectCheckboxChange = (fieldId, event) => {
        let value = event.target.value;
        if (value === "1") {
            value = ["1"];
        } else if (value === "0") {
            value = ["0"];
        } else {
            value = [];
        }
        this.onCategoriesChange(fieldId, value);
    };

    onCategoriesChange = (fieldId, values) => {
        const newValues = {};
        newValues[String(fieldId)] = values;
        const finalValues = _.omit(
            _.extend({}, this.state.objectCategories, newValues),
            (value) => _.isNullish(value) || (_.isArray(value) && value.length === 0)
        );
        this.setState({
            objectCategories: finalValues,
        });
    };

    isCategorySelected = (fieldId, option) => {
        return (
            this.state.objectCategories[String(fieldId)] &&
            this.state.objectCategories[String(fieldId)].indexOf(option) > -1
        );
    };

    getCheckboxValue = (checkboxId) => {
        const values = this.state.objectCategories[String(checkboxId)];
        if (values) {
            return values[0];
        }
        return "";
    };

    _presentField = (field) => {
        if (field.title_type && field.title_type.name) {
            return `${field.title_type.name}: ${field.name}`;
        }
        return field.name;
    };

    _renderCategories = (title, categories) => {
        if (!categories || categories.length === 0) {
            return null;
        }
        const options = categories.map((category) => {
            if (!category.categories) {
                return [];
            }
            return category.categories.map((opt) => ({
                value: opt,
                label: opt,
                selected: this.isCategorySelected(category.id, opt),
            }));
        });
        return (
            <div className="settingsLine">
                <label>{title}</label>
                {options.map((cats, index) => (
                    <div className="categoryList" key={index}>
                        <label>{this._presentField(categories[index])}</label>
                        <MultiSelect
                            className="multiSelect"
                            options={cats}
                            onValueChanged={this.onCategoriesChange.bind(
                                this,
                                categories[index].id
                            )}
                        />
                    </div>
                ))}
            </div>
        );
    };

    _renderFieldChooser = (title, fields, selectedValue, onChange) => {
        return (
            <div className="settingsLine">
                <label>{title}</label>
                <select onChange={onChange} value={selectedValue || ""}>
                    <option value={""}>-</option>
                    {fields.map((field) => (
                        <option key={field.id} value={field.id}>
                            {field.name} ({field.extid})
                        </option>
                    ))}
                </select>
            </div>
        );
    };

    _renderCheckboxes = (title, checkboxes) => {
        if (!checkboxes || checkboxes.length === 0) {
            return null;
        }
        return (
            <div className="settingsLine">
                <label>{title}</label>
                {checkboxes.map((checkbox, index) => {
                    const selectedValue = this.getCheckboxValue(checkbox.id);
                    return (
                        <div className="categoryList" key={index}>
                            <label>{this._presentField(checkbox)} </label>
                            <select
                                onChange={this.onObjectCheckboxChange.bind(
                                    this,
                                    checkboxes[index].id
                                )}
                                value={selectedValue}
                            >
                                <option value={""}>-</option>
                                <option value={1}>{Language.get("dynamic_field_yes")}</option>
                                <option value={0}>{Language.get("dynamic_field_no")}</option>
                            </select>
                        </div>
                    );
                })}
            </div>
        );
    };

    render() {
        const selectOptions: Array<{ selected?: boolean; value: string | number; label: string }> =
            getStatus().map((option) => ({
                selected: this.state.orderStatus.indexOf(option.id) !== -1,
                value: option.id,
                label: option.label,
            }));

        const SPLICE_START = 3;
        selectOptions.splice(SPLICE_START, 0, {
            value: MultiSelectDivider,
            label: MultiSelectDivider,
        });

        selectOptions.splice(SPLICE_START + SPLICE_START, 0, {
            value: MultiSelectDivider,
            label: MultiSelectDivider,
        });

        selectOptions.splice(SPLICE_START * SPLICE_START, 0, {
            value: MultiSelectDivider,
            label: MultiSelectDivider,
        });

        const search = getSearch();
        const searchOptions = Object.keys(search).map((option, index) => ({
            selected: this.state.objectSearch.indexOf(index) !== -1,
            value: index,
            label: search[index].label,
        }));

        const includeMembers = getIncludeMembers();
        const memberOptions = Object.keys(includeMembers).map((option, index) => ({
            selected: this.state.includeMembers === index,
            value: index,
            label: includeMembers[index].label,
        }));

        return (
            <div>
                <h3 className="core-header">{Language.get("nc_list_search_settings_title")}</h3>

                <ListSettingsStore
                    searchSettings={this.state}
                    // eslint-disable-next-line no-shadow
                    onChange={(search) => this.setState(search)}
                    getSavedSettings={this.props.getSavedSettings}
                    saveSettings={this.props.saveSettings}
                    removeSettings={this.props.removeSettings}
                    saveDefaultSettings={this.props.saveDefaultSettings}
                />

                <div className="settingsContainer">
                    <div className="settingsLine">
                        <label>{Language.get("dynamic_reserv_list_members")}</label>
                        <select
                            onChange={this.onIncludeMembersChange}
                            value={this.state.includeMembers}
                        >
                            {memberOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <hr />

                    <div className="settingsLine">
                        <label>{Language.get("dynamic_reserv_list_permission")}</label>
                        <select onChange={this.onPermissionChange} value={this.state.permission}>
                            <option value={RC.PERMISSION.READ} key={RC.PERMISSION.READ}>
                                {Language.get("dynamic_reserv_list_read")}
                            </option>
                            <option value={RC.PERMISSION.MODIFY} key={RC.PERMISSION.MODIFY}>
                                {Language.get("dynamic_reserv_list_modify")}
                            </option>
                        </select>
                    </div>

                    <hr />

                    <div className="settingsLine">
                        <label>{Language.get("dynamic_reserv_list_status_menu")}</label>
                        <MultiSelect
                            className="multiSelect"
                            options={selectOptions}
                            onValueChanged={this.onStatusChange}
                        />
                    </div>

                    <hr />

                    <div className="settingsLine">
                        <label>{Language.get("dynamic_reserv_list_object_search")}</label>
                        <MultiSelect
                            className="multiSelect"
                            options={searchOptions}
                            onValueChanged={this.onObjectSearchChange}
                        />
                    </div>

                    <hr />

                    {this._renderCategories(
                        Language.get("nc_lists_object_categories"),
                        this.props.availableObjectCategories
                    )}

                    {this._renderCheckboxes(
                        Language.get("nc_lists_object_checkboxes"),
                        this.props.availableObjectCheckboxes
                    )}

                    <hr />

                    {this._renderFieldChooser(
                        Language.get("nc_order_list_map_description_to"),
                        this.props.reservationFields,
                        this.state.descriptionField,
                        this.onDescriptionFieldChange
                    )}

                    {this._renderFieldChooser(
                        Language.get("nc_order_list_map_comment_to"),
                        this.props.reservationFields,
                        this.state.commentField,
                        this.onCommentFieldChange
                    )}

                    <div className="settingsLine">
                        <table>
                            <tbody>
                                <tr>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={this.state.useFactor}
                                            onChange={this.onFactorToggle}
                                        />
                                    </td>
                                    <td>{Language.get("nc_order_list_use_minutes_per_week")}</td>
                                </tr>
                                <tr>
                                    <td />
                                    <td>
                                        {Language.get("cal_res_side_view_num_weeks")}
                                        <NumberInput
                                            classNames={"smallInput"}
                                            disabled={!this.state.useFactor}
                                            defaultValue={this.state.factor}
                                            onUpdate={this.onFactorChange}
                                            decimals={1}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    static getDefaultOrderStatus = () => {
        return getStatus()
            .filter((status) => status.isDefault === true)
            .map((status) => status.id);
    };
}

export default OrderListSettings;
